import { Link } from "react-router-dom";

export interface ForecastLike {
  id: number;
  slug: string;
  title: string;
  categoryName: string;
  stage: string;
  stageTone: string;
  heat: number;
  readTime: number;
  date: string;
  author: string;
  cover: string;
  excerpt: string;
  tags: string[];
}

export function stageBadgeClass(tone: string) {
  if (tone === "primary") return "bg-primary-100 text-primary-800 border-primary-200";
  if (tone === "accent") return "bg-accent-100 text-accent-900 border-accent-200";
  return "bg-secondary-100 text-secondary-900 border-secondary-200";
}

interface ForecastCardProps {
  item: ForecastLike;
  variant?: "default" | "horizontal" | "compact";
}

export default function ForecastCard({ item, variant = "default" }: ForecastCardProps) {
  if (variant === "horizontal") {
    return (
      <Link
        to={`/forecast/${item.id}`}
        className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 sm:flex-row"
      >
        <div className="relative h-44 w-full shrink-0 overflow-hidden sm:h-full sm:w-56">
          <img
            src={item.cover}
            alt={`${item.title} 前瞻配图`}
            title={`${item.title} - 看料大瓜预告前瞻`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3 rounded-md bg-background-50/90 px-2 py-1 text-[11px] font-semibold text-foreground-800">
            {item.categoryName}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-4 md:p-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${stageBadgeClass(item.stageTone)}`}>
              {item.stage}
            </span>
            <span className="text-[11px] text-foreground-500">{item.date}</span>
          </div>
          <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
            {item.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{item.excerpt}</p>
          <div className="mt-4 flex items-center gap-4 text-[11px] text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" />
              热度 {item.heat}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {item.readTime} 分钟
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              {item.author}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link to={`/forecast/${item.id}`} className="group flex items-start gap-4 py-4">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 font-heading text-sm font-bold text-primary-600">
          {item.id}
        </span>
        <span className="flex flex-col">
          <span className="font-heading text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
            {item.title}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-3 text-[11px] text-foreground-500">
            <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
              {item.categoryName}
            </span>
            <span>{item.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" />
              {item.heat}
            </span>
          </span>
        </span>
      </Link>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/forecast/${item.id}`} className="relative block h-48 overflow-hidden">
        <img
          src={item.cover}
          alt={`${item.title} 前瞻配图`}
          title={`${item.title} - 看料大瓜预告前瞻`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className={`absolute left-3 top-3 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${stageBadgeClass(item.stageTone)}`}>
          {item.stage}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center gap-2 text-[11px] font-medium text-foreground-500">
          <span className="text-primary-600">{item.categoryName}</span>
          <span className="h-1 w-1 rounded-full bg-background-400" />
          <span>{item.date}</span>
        </div>
        <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          <Link to={`/forecast/${item.id}`}>{item.title}</Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{item.excerpt}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {item.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="rounded-md bg-background-100 px-2 py-1 text-[11px] text-foreground-700">
              #{tag}
            </span>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" />
            热度 {item.heat}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {item.readTime} 分钟
          </span>
        </div>
      </div>
    </article>
  );
}